import { z } from 'zod'
import { serviceFetch } from '../http/serviceLayerClient'

export type HelpArticle = {
  id: string
  slug: string
  title: string
  content: string
  category?: string | null
  orderIndex?: number | null
}

const HelpArticleRow = z.object({
  id: z.union([z.string(), z.number()]),
  slug: z.string(),
  title: z.string(),
  content: z.string(),
  category: z.string().nullable().optional(),
  order_index: z.number().nullable().optional(),
})

function rowToArticle(row: unknown): HelpArticle {
  const r = HelpArticleRow.safeParse(row)
  if (!r.success) throw new Error('Invalid help article row')
  const x = r.data
  return {
    id: String(x.id),
    slug: x.slug,
    title: x.title,
    content: x.content,
    category: x.category ?? null,
    orderIndex: x.order_index ?? null,
  }
}

function articleToInsertRow(article: Omit<HelpArticle, 'id'>): Record<string, unknown> {
  return {
    slug: article.slug,
    title: article.title,
    content: article.content,
    category: article.category ?? null,
    order_index: article.orderIndex ?? null,
  }
}

function articleToUpdateRow(updates: Partial<Omit<HelpArticle, 'id'>>): Record<string, unknown> {
  const row: Record<string, unknown> = {}
  if (updates.slug !== undefined) row.slug = updates.slug
  if (updates.title !== undefined) row.title = updates.title
  if (updates.content !== undefined) row.content = updates.content
  if (updates.category !== undefined) row.category = updates.category
  if (updates.orderIndex !== undefined) row.order_index = updates.orderIndex
  return row
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 60)
}

export async function listHelpArticles(): Promise<HelpArticle[]> {
  // FAIL-LOUD (FR-FE-CODEGEN-FAIL-LOUD-DEFENSIVE-READ-STD): a fetch failure
  // propagates so the caller renders an Error state. A genuine 200 with no
  // articles returns []. We never catch-and-return-[] (that masked a failed
  // read as "no help articles" — the all-dark / silent-missing-data class).
  const res = await serviceFetch<{ data?: unknown; articles?: unknown[] }>('/v1/help/articles', { method: 'GET' })
  const body = res.data as { data?: unknown; articles?: unknown[] }
  const rows = body?.articles ?? (Array.isArray(body?.data) ? body.data : []) ?? []
  return (rows as unknown[]).map(rowToArticle)
}

export async function createHelpArticle(article: Omit<HelpArticle, 'id'>): Promise<HelpArticle> {
  const payload = articleToInsertRow(article)
  const res = await serviceFetch<{ data?: unknown; article?: unknown }>('/v1/help/articles', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const row = (res.data as any)?.article ?? (res.data as any)
  return rowToArticle(row)
}

export async function updateHelpArticle(id: string, updates: Partial<Omit<HelpArticle, 'id'>>): Promise<HelpArticle> {
  const payload = articleToUpdateRow(updates)
  const res = await serviceFetch<{ data?: unknown; article?: unknown }>(`/v1/help/articles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  const row = (res.data as any)?.article ?? (res.data as any)
  return rowToArticle(row)
}

export async function deleteHelpArticle(id: string): Promise<void> {
  await serviceFetch(`/v1/help/articles/${encodeURIComponent(id)}`, { method: 'DELETE' })
}














